import React from "react";
import { useTranslation } from "react-i18next";
import {
  Sunrise,
  Sun,
  Moon,
  Utensils,
  Calendar,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";

const TodayAvailabilityCard = ({ availability, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 rounded w-20"></div>
              <div className="h-6 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!availability) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {t("todayAvailability")}
        </h3>
        <div className="text-center py-4">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 mt-2">{t("noAvailabilityData")}</p>
        </div>
      </div>
    );
  }

  const getMealTypeIcon = (mealType) => {
    switch (mealType) {
      case "BREAKFAST":
        return <Sunrise className="w-5 h-5 text-orange-500" />;
      case "LUNCH":
        return <Sun className="w-5 h-5 text-yellow-500" />;
      case "DINNER":
        return <Moon className="w-5 h-5 text-blue-500" />;
      default:
        return <Utensils className="w-5 h-5 text-gray-500" />;
    }
  };

  const getMealTypeName = (mealType) => {
    switch (mealType) {
      case "BREAKFAST":
        return t("breakfast");
      case "LUNCH":
        return t("lunch");
      case "DINNER":
        return t("dinner");
      case "SNACK":
        return t("snack");
      case "SIDE":
        return t("sides");
      default:
        return mealType;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {t("todayAvailability")}
        </h3>
        <span className="text-sm text-gray-500">
          {availability.totalAvailableMeals} {t("totalMeals")}
        </span>
      </div>

      <div className="space-y-3">
        {availability.mealTypeAvailabilities?.map((mealType) => (
          <div
            key={mealType.mealType}
            className="flex items-center justify-between"
          >
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              {getMealTypeIcon(mealType.mealType)}
              <span className="text-sm font-medium text-gray-700">
                {getMealTypeName(mealType.mealType)}
              </span>
            </div>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <span className="text-sm font-semibold text-gray-900">
                {mealType.availableCount}
              </span>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  mealType.hasMinimumCoverage
                    ? "bg-green-100 text-green-800"
                    : "bg-orange-100 text-orange-800"
                }`}
              >
                {mealType.hasMinimumCoverage ? (
                  <CheckCircle className="w-3 h-3" />
                ) : (
                  <AlertTriangle className="w-3 h-3" />
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      {availability.missingMealTypes?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {t("recommendations")}
          </h4>
          <div className="space-y-1">
            {availability.missingMealTypes.map((missing, index) => (
              <p
                key={index}
                className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded"
              >
                {missing.recommendation}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayAvailabilityCard;
